/**
 * La scheda di un corso sul telefono (7/10/2026): risponde a "a che punto è il
 * corso, e cosa c'è da fare oggi in aula?".
 *
 * Sul computer è una pagina di gestione con sei linguette (selezione a
 * colonne, registro presenze a griglia, persone, esiti, amministrazione):
 * a 390px le tabelle uscivano dallo schermo. Qui:
 *   1. il corso, la fase (si cambia toccando la riga, come gli step del
 *      computer) e il passo successivo;
 *   2. il registro di UNA lezione (quella di oggi, se c'è): un discente per
 *      riga, si tocca e si segna presente, in ritardo, assente o giustificato,
 *      oppure "tutti presenti". È il gesto da aula, quello del telefono;
 *   3. le prossime lezioni, i discenti, i docenti, i documenti.
 * La selezione a colonne, la griglia di tutte le presenze, valutazioni,
 * rendicontazione e budget restano al computer: lo dice una riga sola.
 * Stesse chiamate e stesse chiavi di cache del computer.
 */
import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { api } from '../../lib/api';
import { toast } from '../../stores/toast.store';
import { useModalStore } from '../../stores/modal.store';
import { Icon } from '../../components/ui/Icon';
import { DesktopOnly } from '../../components/ui/DesktopOnly';
import { MEmpty, MGroup, MHead, MHero, MMore, MPage, MRow, MScelte, iniziali, leggibile } from '../../components/mobile/MobileKit';
import { FASE_CANDIDATO, FASE_CORSO } from './TalentMobile';
import './talent-mobile.css';

interface CourseFull {
  id: string; name: string; type: 'FORMATEMP' | 'FONDIMPRESA' | 'INTERNO'; status: string; mode: string;
  partnerName: string | null; startDate: string; endDate: string; hoursTotal: number;
  enrolledCount: number; revenue: string; cost?: number; description: string | null; objectives: string | null;
  client: { id: string; legalName: string } | null;
  teachers: Array<{ id: string; user: { firstName: string; lastName: string; initials: string } | null; supplier: any }>;
  lessons: Array<{ id: string; date: string; startTime: string; endTime: string; topic: string; room: string | null }>;
  enrollments: Array<{ id: string; candidate: { id: string; firstName: string; lastName: string; city: string | null; stage: string } }>;
  documents: any[];
  assessments: any[];
}
interface AttendanceMatrix {
  lessons: Array<{ id: string; date: string; topic: string; startTime: string; endTime: string }>;
  rows: Array<{ enrollmentId: string; candidate: { id: string; firstName: string; lastName: string }; cells: Array<{ lessonId: string; status: string | null }>; attendancePct: number }>;
}

const TIPO: Record<string, string> = { FORMATEMP: 'Formatemp', FONDIMPRESA: 'Fondimpresa', INTERNO: 'Interno' };
const PRESENZA: Array<{ v: string; l: string }> = [
  { v: 'PRESENTE', l: 'Presente' }, { v: 'RITARDO', l: 'In ritardo' }, { v: 'ASSENTE', l: 'Assente' }, { v: 'GIUSTIFICATO', l: 'Assente giustificato' },
];
const nomePresenza = (s: string | null) => PRESENZA.find((p) => p.v === s)?.l ?? 'da segnare';
const giorno = (iso: string) => new Date(iso).toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' }).replace('.', '');
const stessoGiorno = (iso: string) => new Date(iso).toDateString() === new Date().toDateString();

export function CorsoMobile() {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [aperto, setAperto] = useState<null | 'fase' | 'lezione' | string>(null);
  const [lezioneId, setLezioneId] = useState<string | null>(null);
  const [tutteLezioni, setTutteLezioni] = useState(false);

  const { data: c, isLoading, isError } = useQuery({
    queryKey: ['course', id],
    queryFn: () => api.get<CourseFull>(`/courses/${id}`),
    enabled: !!id,
    retry: false,
  });
  const { data: registro } = useQuery({
    queryKey: ['attendance-matrix', id],
    queryFn: () => api.get<AttendanceMatrix>(`/courses/${id}/attendance-matrix`),
    enabled: !!id,
  });

  const patchStatus = useMutation({
    mutationFn: (status: string) => api.patch(`/courses/${id}`, { status }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['course', id] }); qc.invalidateQueries({ queryKey: ['courses'] }); toast.ok('Fase del corso aggiornata'); },
    onError: (e: any) => toast.alert('Fase non aggiornata', e?.message ?? 'Riprova'),
  });
  // Le stesse due chiamate del registro presenze del computer.
  const segna = useMutation({
    mutationFn: ({ lessonId, enrollmentId, status }: { lessonId: string; enrollmentId: string; status: string }) =>
      api.post(`/courses/lessons/${lessonId}/attendance`, { enrollmentId, status }),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['attendance-matrix', id] }),
    onError: (e: any) => toast.alert('Presenza non segnata', e?.message ?? 'Riprova'),
  });
  const tuttiPresenti = useMutation({
    mutationFn: (lessonId: string) => api.post(`/courses/lessons/${lessonId}/attendance-bulk`, {
      entries: (registro?.rows ?? []).map((r) => ({ enrollmentId: r.enrollmentId, status: 'PRESENTE' })),
    }),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['attendance-matrix', id] }); toast.ok('Tutti presenti'); },
    onError: (e: any) => toast.alert('Presenze non segnate', e?.message ?? 'Riprova'),
  });

  // La lezione del registro: quella di oggi; se non c'è, l'ultima fatta; se
  // il corso non è ancora cominciato, la prima.
  const lezioneScelta = useMemo(() => {
    const lezioni = registro?.lessons ?? [];
    if (lezioneId) return lezioni.find((l) => l.id === lezioneId) ?? null;
    return lezioni.find((l) => stessoGiorno(l.date))
      ?? [...lezioni].filter((l) => new Date(l.date) < new Date()).sort((a, b) => b.date.localeCompare(a.date))[0]
      ?? lezioni[0] ?? null;
  }, [registro, lezioneId]);

  if (isError) return <MPage><MEmpty>Corso non trovato: forse è stato eliminato, o il link è sbagliato.</MEmpty></MPage>;
  if (isLoading || !c) return <MPage><MEmpty>Carico il corso…</MEmpty></MPage>;

  const fondimpresa = c.type === 'FONDIMPRESA';
  // Gli step del computer: Fondimpresa rendiconta, gli altri fanno placement.
  const fasi = FASE_CORSO.filter(([k]) => (fondimpresa ? k !== 'PLACEMENT' : k !== 'RENDICONTAZIONE'));
  const nomeFase = FASE_CORSO.find(([k]) => k === c.status)?.[1] ?? c.status.replace(/_/g, ' ').toLowerCase();
  const fatte = c.lessons.filter((l) => new Date(l.date) < new Date()).length;
  const prossime = c.lessons.filter((l) => new Date(l.date) >= new Date()).sort((a, b) => a.date.localeCompare(b.date));
  const media = registro?.rows.length ? Math.round(registro.rows.reduce((a, r) => a + r.attendancePct, 0) / registro.rows.length) : null;
  const pronti = c.enrollments.filter((e) => e.candidate.stage === 'PRONTO_PER_PLACEMENT').length;
  const passo = c.status === 'ISCRIZIONI' ? { t: 'Porta i candidati nel corso', s: 'Il corso raccoglie le iscrizioni: aggiungi i candidati.', fn: () => useModalStore.getState().open('addCourseCandidate', { courseId: c.id }) }
    : c.status === 'AULA' && lezioneScelta ? { t: 'Segna le presenze', s: `Il registro di ${giorno(lezioneScelta.date)} è qui sotto.`, fn: () => document.getElementById('registro')?.scrollIntoView({ behavior: 'smooth' }) }
    : null;
  const lezioniVisibili = tutteLezioni ? prossime : prossime.slice(0, 4);

  return (
    <div className="tlm">
      <MPage>
        <MHead
          title={c.name}
          sub={[TIPO[c.type] ?? c.type, c.mode ? c.mode.replace(/_/g, ' ').toLowerCase() : null, `${c.hoursTotal} ore`, c.partnerName, c.client ? leggibile(c.client.legalName) : null].filter(Boolean).join(' · ')}
        />

        <MHero
          label="Fase del corso"
          value={<span className="tlm-frase">{nomeFase}</span>}
          sub={[
            c.lessons.length ? `${fatte} di ${c.lessons.length} lezioni fatte` : 'nessuna lezione in calendario',
            media != null ? `presenze ${media}%` : null,
            pronti ? `${pronti} ${pronti === 1 ? 'pronto' : 'pronti'} per un'azienda` : null,
          ].filter(Boolean).join(' · ')}
        />

        <MGroup title="A che punto è">
          <MRow icon="talent" title="Fase" trail={nomeFase} trailTone="mute" onClick={() => setAperto('fase')} />
          {prossime[0] && <MRow icon="calendar" title="Prossima lezione" sub={`${giorno(prossime[0].date)} · ${prossime[0].startTime}–${prossime[0].endTime} · ${prossime[0].topic}`} />}
          {passo && <MRow icon="arrowRight" tone="ink" title={passo.t} sub={passo.s} onClick={passo.fn} />}
        </MGroup>

        {lezioneScelta && (registro?.rows.length ?? 0) > 0 && (
          <div id="registro">
            <MGroup
              title={`Registro di ${stessoGiorno(lezioneScelta.date) ? 'oggi' : giorno(lezioneScelta.date)}`}
              action={(registro?.lessons.length ?? 0) > 1 ? { label: 'Altra lezione', onClick: () => setAperto('lezione') } : undefined}
              note={`Lezione: ${lezioneScelta.topic}, ${lezioneScelta.startTime}–${lezioneScelta.endTime}.`}
            >
              {registro!.rows.map((r) => {
                const cella = r.cells.find((x) => x.lessonId === lezioneScelta.id);
                const nome = `${r.candidate.firstName} ${r.candidate.lastName}`;
                return (
                  <MRow
                    key={r.enrollmentId}
                    text={iniziali(nome)}
                    round
                    tone={cella?.status === 'ASSENTE' ? 'alert' : undefined}
                    title={nome}
                    sub={`presenze ${r.attendancePct}%`}
                    trail={nomePresenza(cella?.status ?? null)}
                    trailTone="mute"
                    onClick={() => setAperto(`presenza:${r.enrollmentId}`)}
                  />
                );
              })}
              <button type="button" className="tlm-azione" disabled={tuttiPresenti.isPending} onClick={() => tuttiPresenti.mutate(lezioneScelta.id)}>
                <Icon name="check" size={15} /> Segna tutti presenti
              </button>
            </MGroup>
          </div>
        )}

        <MGroup title="Prossime lezioni" count={prossime.length || undefined}>
          {lezioniVisibili.length
            ? lezioniVisibili.map((l) => (
              <MRow key={l.id} icon="calendar" title={l.topic} sub={[giorno(l.date), `${l.startTime}–${l.endTime}`, l.room].filter(Boolean).join(' · ')} />
            ))
            : <MEmpty>{c.lessons.length ? 'Le lezioni sono tutte fatte.' : 'Nessuna lezione in calendario.'}</MEmpty>}
          {prossime.length > 4 && <MMore label={tutteLezioni ? 'Mostra meno' : `Vedi tutte le ${prossime.length}`} onClick={() => setTutteLezioni((v) => !v)} />}
        </MGroup>

        <MGroup title="Discenti" count={c.enrollments.length || undefined} action={{ label: 'Aggiungi', onClick: () => useModalStore.getState().open('addCourseCandidate', { courseId: c.id }) }}>
          {c.enrollments.length ? c.enrollments.map((e) => {
            const nome = `${e.candidate.firstName} ${e.candidate.lastName}`;
            return (
              <MRow
                key={e.id}
                text={iniziali(nome)}
                round
                title={nome}
                sub={[FASE_CANDIDATO.find(([k]) => k === e.candidate.stage)?.[1] ?? e.candidate.stage.replace(/_/g, ' ').toLowerCase(), e.candidate.city ? leggibile(e.candidate.city) : null].filter(Boolean).join(' · ')}
                onClick={() => navigate(`/candidates/${e.candidate.id}`)}
              />
            );
          }) : <MEmpty>Nessun iscritto.</MEmpty>}
        </MGroup>

        <MGroup title="Docenti" count={c.teachers.length || undefined}>
          {c.teachers.length ? c.teachers.map((t) => {
            const nome = t.user ? `${t.user.firstName} ${t.user.lastName}` : t.supplier?.fullName ?? 'Docente';
            return <MRow key={t.id} text={iniziali(nome)} round title={nome} sub={t.user ? 'interno' : 'esterno'} />;
          }) : <MEmpty>Nessun docente assegnato.</MEmpty>}
        </MGroup>

        <MGroup title="Documenti" count={c.documents.length || undefined} action={{ label: 'Carica', onClick: () => useModalStore.getState().open('uploadDoc', { scope: 'course', scopeId: c.id }) }}>
          {c.documents.length ? c.documents.slice(0, 6).map((d: any) => (
            <MRow key={d.id} icon="file" title={d.name ?? 'Documento'} sub={d.createdAt ? giorno(d.createdAt) : undefined} />
          )) : <MEmpty>Nessun documento.</MEmpty>}
        </MGroup>

        <DesktopOnly
          cosa="Lavorare sulla selezione a colonne, sulla griglia di tutte le presenze, su valutazioni, rendicontazione e budget"
          perche="sono tabelle larghe e procedure da fare con calma. Dal telefono si segna il registro di una lezione e si consulta il resto."
        ><></></DesktopOnly>
      </MPage>

      <MScelte
        open={aperto === 'fase'}
        onClose={() => setAperto(null)}
        titolo="Fase del corso"
        opzioni={fasi.map(([k, l]) => ({ v: k, l }))}
        valore={c.status}
        onScegli={(v) => { setAperto(null); if (v !== c.status) patchStatus.mutate(v); }}
      />
      <MScelte
        open={aperto === 'lezione'}
        onClose={() => setAperto(null)}
        titolo="Lezione del registro"
        opzioni={[...(registro?.lessons ?? [])].sort((a, b) => b.date.localeCompare(a.date)).map((l) => ({ v: l.id, l: giorno(l.date), sub: l.topic }))}
        valore={lezioneScelta?.id ?? ''}
        onScegli={(v) => { setLezioneId(v); setAperto(null); }}
      />
      {lezioneScelta && (registro?.rows ?? []).map((r) => (
        <MScelte
          key={r.enrollmentId}
          open={aperto === `presenza:${r.enrollmentId}`}
          onClose={() => setAperto(null)}
          titolo={`${r.candidate.firstName} ${r.candidate.lastName}`}
          nota={`${giorno(lezioneScelta.date)} · ${lezioneScelta.topic}`}
          opzioni={PRESENZA}
          valore={r.cells.find((x) => x.lessonId === lezioneScelta.id)?.status ?? ''}
          onScegli={(v) => { setAperto(null); segna.mutate({ lessonId: lezioneScelta.id, enrollmentId: r.enrollmentId, status: v }); }}
        />
      ))}
    </div>
  );
}
